/**
 * Worlds Page
 *
 * The "front door" gallery, discovery-first: page header → the worlds gallery
 * (featured + 2x2) ABOVE the fold → a creation band at the BOTTOM. The creation
 * band leads with a no-code "Create a World" path and demotes the SDK to a
 * secondary developer line.
 *
 * Starting a world opens CampaignWizard prefilled with that playset; "Create a
 * World" opens the SAME wizard fresh (no playset) for a blank/custom world.
 *
 * PUBLIC: this page renders both inside the app (logged-in, via App's `#worlds`
 * branch) AND as a pre-login front door (via main.tsx's `#worlds` branch, OUTSIDE
 * AuthGuard). When a logged-OUT visitor acts on a world it doesn't open the wizard
 * — instead it stashes the chosen playset as a pending intent and hands off to
 * signup; the app consumes that intent after auth and opens the wizard prefilled.
 * See `WORLD_INTENT_KEY` + App.tsx's post-auth consumer.
 */

import { useEffect, useState } from 'react';
import { PlaysetId, PLAYSETS } from '@machina/core';
import { useAuthStore } from '../../stores/authStore';
import { BladeIcon, GouldCorner } from '../Ornaments';
import { Icon } from '../Icon';
import { CampaignWizard } from '../CampaignWizard';
import { CreateWorldBand } from './CreateWorldBand';
import { WorldsGallery } from './WorldsGallery';

/**
 * sessionStorage key for the intent-preserving signup handoff. A logged-out
 * visitor's chosen world is stashed here, then consumed by App.tsx once the user
 * is authenticated (mirrors authStore's `machina:pending-beta-key` OAuth pattern).
 * Value: a `PlaysetId`, or the literal `'__create__'` for the fresh/custom path.
 */
export const WORLD_INTENT_KEY = 'machina:pendingWorldIntent';

/** Sentinel stored for the no-code "Create a World" (fresh, no playset) path. */
export const WORLD_INTENT_CREATE = '__create__';

// =============================================================================
// SEO / GEO (Phase 1 — client-side only, no SSR, no deps)
// =============================================================================

const WORLDS_TITLE = 'Machina — Worlds';
const WORLDS_DESCRIPTION =
  'Browse Machina’s worlds — AI-native d20 RPG settings you can drop into instantly. ' +
  'Pick a world and start a campaign, or describe your own and Machina builds it for you.';

/** id of the injected JSON-LD <script>, so we can find + remove it on unmount. */
const WORLDS_JSONLD_ID = 'machina-worlds-jsonld';

/** Remembers the pre-mount title/description so we can restore on unmount. */
let priorTitle: string | null = null;
let priorDescription: string | null = null;

function getDescriptionMeta(): HTMLMetaElement | null {
  return document.querySelector('meta[name="description"]');
}

/**
 * Build a schema.org CollectionPage whose mainEntity is an ItemList of the
 * worlds (name + description per PLAYSET). Static data only.
 */
function buildWorldsJsonLd(): string {
  const worlds = Object.values(PLAYSETS);
  const data = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: WORLDS_TITLE,
    description: WORLDS_DESCRIPTION,
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: worlds.length,
      itemListElement: worlds.map((w, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'CreativeWork',
          name: w.name,
          description: w.description,
        },
      })),
    },
  };
  return JSON.stringify(data);
}

/** Set the Worlds title/description and inject the JSON-LD script. */
function applyWorldsSeo(): void {
  if (priorTitle === null) priorTitle = document.title;
  document.title = WORLDS_TITLE;

  const meta = getDescriptionMeta();
  if (meta) {
    if (priorDescription === null) priorDescription = meta.getAttribute('content');
    meta.setAttribute('content', WORLDS_DESCRIPTION);
  }

  if (!document.getElementById(WORLDS_JSONLD_ID)) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = WORLDS_JSONLD_ID;
    script.textContent = buildWorldsJsonLd();
    document.head.appendChild(script);
  }
}

/** Restore the prior title/description and remove the JSON-LD script. */
function restoreWorldsSeo(): void {
  if (priorTitle !== null) {
    document.title = priorTitle;
    priorTitle = null;
  }
  const meta = getDescriptionMeta();
  if (meta && priorDescription !== null) {
    meta.setAttribute('content', priorDescription);
  }
  priorDescription = null;

  document.getElementById(WORLDS_JSONLD_ID)?.remove();
}

const GOLD = '#d4af37';
const GOLD_DIM = 'rgba(212, 175, 55, 0.3)';

interface WorldsPageProps {
  /** Called when a campaign is created from the wizard — typically enter it. */
  onEnterCampaign: (campaignId: string) => void;
  /** Navigate back (to the lobby in-app, or the landing/login pre-auth). */
  onBack: () => void;
  /**
   * Called when a logged-OUT visitor acts on a world. Receives the chosen
   * `PlaysetId`, or `null` for the fresh/custom "Create a World" path. The owner
   * (PublicWorldsPage) stashes the intent and routes to signup. When omitted
   * (the in-app, logged-in case) the wizard opens inline as before.
   */
  onSignupHandoff?: (playsetId: PlaysetId | null) => void;
  /**
   * Auto-open the wizard on mount, consuming a pending signup-handoff intent.
   * `'fresh'` opens the blank/custom wizard; a `PlaysetId` opens it prefilled.
   * Set by App.tsx after a logged-out visitor signs up (see WORLD_INTENT_KEY).
   */
  autoOpenIntent?: PlaysetId | 'fresh';
  /** Fired once after `autoOpenIntent` is consumed so the owner can clear it. */
  onIntentConsumed?: () => void;
}

/**
 * True on narrow viewports. Inline styles can't carry media queries, so the
 * header reads this to center the wordmark and collapse the Back button to an
 * icon-only control on phones.
 */
function useIsNarrow(maxWidthPx = 560): boolean {
  const query = `(max-width: ${maxWidthPx}px)`;
  const [narrow, setNarrow] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setNarrow(e.matches);
    setNarrow(mq.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [query]);

  return narrow;
}

export function WorldsPage({ onEnterCampaign, onBack, onSignupHandoff, autoOpenIntent, onIntentConsumed }: WorldsPageProps) {
  const isNarrow = useIsNarrow();
  // `null` playset = a fresh, blank/custom world (the no-code "Create a World"
  // path); a PlaysetId = "Start Campaign in this World" prefilled from a playset.
  const [wizardPlayset, setWizardPlayset] = useState<PlaysetId | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  // Logged-out visitors are handed off to signup with their chosen world stashed
  // as a pending intent; logged-in visitors open the wizard inline. Campaign
  // creation itself stays behind auth (CampaignWizard's `if (!user?.id)` guard) —
  // this only preserves which world they wanted.
  const startOrHandoff = (playsetId: PlaysetId | null) => {
    if (onSignupHandoff && !useAuthStore.getState().user) {
      onSignupHandoff(playsetId);
      return;
    }
    setWizardPlayset(playsetId);
    setWizardOpen(true);
  };

  const handleStartWorld = (playsetId: PlaysetId) => startOrHandoff(playsetId);

  // No-code "Create a World": same wizard, but FRESH (no initialPlaysetId).
  const handleCreateWorld = () => startOrHandoff(null);

  // Relabel the gallery/band CTAs for logged-out visitors ("Sign up to play")
  // so the single gold CTA reads as the signup funnel rather than "creates now".
  const loggedOut = !!onSignupHandoff && !useAuthStore.getState().user;

  // SEO / GEO (Phase 1, no SSR): set the document title + meta description and
  // emit a CollectionPage/ItemList JSON-LD built from the static PLAYSETS data.
  // Pure client-side DOM — no deps, no DB. Restored on unmount so SPA nav back
  // into the app doesn't leave the Worlds title/JSON-LD behind.
  useEffect(() => {
    applyWorldsSeo();
    return restoreWorldsSeo;
  }, []);

  // Consume a pending signup-handoff intent: open the wizard prefilled (or fresh)
  // exactly once on mount. App.tsx clears the sessionStorage key before passing
  // this in, so a refresh won't re-trigger.
  useEffect(() => {
    if (!autoOpenIntent) return;
    setWizardPlayset(autoOpenIntent === 'fresh' ? null : autoOpenIntent);
    setWizardOpen(true);
    onIntentConsumed?.();
  }, [autoOpenIntent, onIntentConsumed]);

  return (
    <div style={styles.container}>
      <div style={styles.bgPattern} />

      {/* Header */}
      <header style={styles.header}>
        <GouldCorner size={28} color={GOLD_DIM} style={{ position: 'absolute', top: 8, left: 8 }} />
        <GouldCorner size={28} color={GOLD_DIM} style={{ position: 'absolute', top: 8, right: 8, transform: 'scaleX(-1)' }} />

        {/* On phones the Back button collapses to an icon so the wordmark can sit
            dead-center; on desktop it keeps its label + fixed width to balance the
            right-hand spacer. */}
        <button
          onClick={onBack}
          style={isNarrow ? styles.backBtnIcon : styles.backBtn}
          aria-label="Back"
          title="Back"
        >
          <Icon name="chevron-left" size={16} />
          {!isNarrow && 'Back'}
        </button>

        <div style={styles.headerCenter}>
          <BladeIcon size={32} />
          <h1 style={styles.title}>WORLDS</h1>
        </div>

        {/* Spacer to balance the back button so the wordmark stays centered. On
            mobile it matches the icon-only button's width. */}
        <div style={{ width: isNarrow ? 36 : 80 }} />
      </header>

      {/* Main — discovery-first: gallery on top, creation band at the bottom. */}
      <main style={styles.main}>
        <WorldsGallery onStartWorld={handleStartWorld} loggedOut={loggedOut} />
        <CreateWorldBand onCreateWorld={handleCreateWorld} loggedOut={loggedOut} />
      </main>

      {/* Wizard. Prefilled with the chosen playset, or fresh for a custom world. */}
      {wizardOpen && (
        <CampaignWizard
          initialPlaysetId={wizardPlayset ?? undefined}
          onClose={() => setWizardOpen(false)}
          onCreated={(id) => {
            setWizardOpen(false);
            onEnterCampaign(id);
          }}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: 'var(--color-background)',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden',
  },

  bgPattern: {
    position: 'absolute',
    inset: 0,
    opacity: 0.02,
    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
    pointerEvents: 'none',
  },

  header: {
    padding: 'var(--spacing-md) var(--spacing-xl)',
    backgroundColor: 'var(--color-surface)',
    borderBottom: `2px solid ${GOLD_DIM}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },

  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-xs)',
    padding: 'var(--spacing-xs) var(--spacing-md)',
    backgroundColor: 'transparent',
    border: `1px solid ${GOLD_DIM}`,
    borderRadius: 'var(--radius-sm)',
    color: GOLD,
    fontSize: 'var(--font-size-sm)',
    fontFamily: 'var(--font-display)',
    cursor: 'pointer',
    width: 80,
    justifyContent: 'center',
  },

  // Icon-only Back on phones so the wordmark can be centered without dead space.
  backBtnIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    padding: 0,
    backgroundColor: 'transparent',
    border: `1px solid ${GOLD_DIM}`,
    borderRadius: 'var(--radius-sm)',
    color: GOLD,
    cursor: 'pointer',
  },

  headerCenter: {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--spacing-sm)',
  },

  title: {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 700,
    letterSpacing: '0.3em',
    color: GOLD,
    textShadow: '0 2px 10px rgba(212, 175, 55, 0.3)',
  },

  main: {
    flex: 1,
    padding: 'var(--spacing-xl)',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
  },
};
